// Sync-token helpers. The token is opaque to the client except for one
// thing: its expiry, which the Provider needs to schedule a proactive
// re-mint at 80% of TTL (protocol spec §Token expiry; control-plane spec
// §Sync connect). Format (control-plane spec §Sync token format):
//   "cairn_sync_" + base64url(protobuf SyncTokenClaims) + "." + base64url(sig)

import { SyncTokenClaims } from "@cairn/proto";

const PREFIX = "cairn_sync_";

/** Decode a base64url string (no padding) to bytes. */
function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/**
 * Parse the claims out of a sync token. Returns null if the token is not a
 * well-formed cairn sync token (the caller then treats it as unusable rather
 * than crashing). Signature is NOT verified here — the server does that; the
 * client only reads `exp` to time its refresh.
 */
export function parseSyncTokenClaims(token: string): SyncTokenClaims | null {
  if (!token.startsWith(PREFIX)) return null;
  const body = token.slice(PREFIX.length);
  const dot = body.indexOf(".");
  if (dot <= 0) return null;
  try {
    return SyncTokenClaims.decode(fromBase64Url(body.slice(0, dot)));
  } catch {
    return null;
  }
}

/** Token expiry in unix milliseconds, or null when unparseable. */
export function tokenExpiryMs(token: string): number | null {
  const claims = parseSyncTokenClaims(token);
  if (!claims || claims.exp === 0) return null;
  return claims.exp * 1000;
}
